import React, { useState } from 'react'
import { CheckIcon } from '@heroicons/react/outline'
import GroceriesService from "../../../services/GroceriesService";
import ClientService from '../../../services/ClientService';
import { calcAll, ageFromBirthday, ACTIVITY_LEVELS, bmiCategory } from '../../../services/HealthCalc';

const MeasurementForm = ({ measurementData, clientId, modalTitle, setIsOpen, setMeasurementsData, deleteMeasurement, addMeasurement, selectedClient }) => {

    const {
        height: heightData,
        size: sizeData,
        bodyMass: bodyMassData,
        fatPercentage: fatPercentageData,
        waterPercentage: waterPercentageData,
        visceralFat: visceralFatData,
        muscleMass: muscleMassData,
        bodyFitnessIndex: bodyFitnessIndexData,
        boneMass: boneMassData,
        baseMetabolism: baseMetabolismData,
        metabolicAge: metabolicAgeData,
        bmi: bmiData,
        upperArm: upperArmData,
        chest: chestData,
        waist: waistData,
        hips: hipsData,
        thighs: thighsData,
        stomach: stomachData,
        measurementDate: measurementDateData,
    } = measurementData;

    const [height, setHeight] = useState(heightData || '');
    const [size, setSize] = useState(sizeData || '');
    const [bodyMass, setBodyMass] = useState(bodyMassData || '');
    const [fatPercentage, setFatPercentage] = useState(fatPercentageData || '');
    const [waterPercentage, setWaterPercentage] = useState(waterPercentageData || '');
    const [visceralFat, setVisceralFat] = useState(visceralFatData || '');
    const [muscleMass, setMuscleMass] = useState(muscleMassData || '');
    const [bodyFitnessIndex, setBodyFitnessIndex] = useState(bodyFitnessIndexData || '');
    const [boneMass, setBoneMass] = useState(boneMassData || '');
    const [baseMetabolism, setBaseMetabolism] = useState(baseMetabolismData || '');
    const [metabolicAge, setMetabolicAge] = useState(metabolicAgeData || '');
    const [bmi, setBmi] = useState(bmiData || '');
    const [upperArm, setUpperArm] = useState(upperArmData || '');
    const [chest, setChest] = useState(chestData || '');
    const [waist, setWaist] = useState(waistData || '');
    const [hips, setHips] = useState(hipsData || '');
    const [thighs, setThighs] = useState(thighsData || '');
    const [stomach, setStomach] = useState(stomachData || '');
    const [measurementDate, setMeasurementDate] = useState(measurementDateData || '');

    // Kalorijski unos (raspon) - racuna se iz TDEE, moze se i rucno korigovati
    const [calorieFrom, setCalorieFrom] = useState(measurementData.calorieFrom || '');
    const [calorieTo, setCalorieTo] = useState(measurementData.calorieTo || '');

    // Odnos makronutrijenata u planu (procenti). Zbir treba da bude 100.
    const [carbsPercent, setCarbsPercent] = useState(measurementData.carbsPercent || '');
    const [proteinPercent, setProteinPercent] = useState(measurementData.proteinPercent || '');
    const [fatPercent, setFatPercent] = useState(measurementData.fatPercent || '');

    // Nivo aktivnosti za TDEE (ne cuva se, sluzi samo za racun)
    const [activityFactor, setActivityFactor] = useState('1.55');
    // Rezultati kalkulatora (BMR i TDEE se prikazuju; BMI se upisuje u polje bmi)
    const [calcResult, setCalcResult] = useState(null);

    const formValues = {
        height,
        size,
        bodyMass,
        fatPercentage,
        waterPercentage,
        visceralFat,
        muscleMass,
        bodyFitnessIndex,
        boneMass,
        baseMetabolism,
        metabolicAge,
        bmi,
        upperArm,
        chest,
        waist,
        hips,
        thighs,
        stomach,
        measurementDate,
        calorieFrom,
        calorieTo,
        carbsPercent,
        proteinPercent,
        fatPercent,
    };

    // Zbir procenata makronutrijenata (za proveru da je 100)
    const macroSum = [carbsPercent, proteinPercent, fatPercent]
        .reduce((sum, v) => sum + (parseFloat(String(v).replace(',', '.')) || 0), 0);
    const macroEntered = [carbsPercent, proteinPercent, fatPercent].some((v) => String(v).trim() !== '');
    const macroValid = Math.round(macroSum) === 100;

    // Godine iz datuma rođenja klijenta (za BMR/TDEE)
    const clientAge = ageFromBirthday(selectedClient && selectedClient.birthday);
    const clientGender = selectedClient && selectedClient.gender;

    // Pokreće kalkulator: BMI, BMR, TDEE. Rezultat prikazuje i upisuje BMI u polje.
    const runCalculator = () => {
        const res = calcAll({
            weight: bodyMass || measurementData.weight,
            height,
            age: clientAge,
            gender: clientGender,
            activityFactor,
        });
        setCalcResult(res);
        if (res.bmi !== null) setBmi(String(res.bmi));
        // Predlog raspona kalorija: TDEE ± ~5% (zaokruženo na 50). Nutricionista
        // ga može ručno prepraviti prema cilju.
        if (res.tdee !== null) {
            const round50 = (n) => Math.round(n / 50) * 50;
            setCalorieFrom(String(round50(res.tdee * 0.95)));
            setCalorieTo(String(round50(res.tdee * 1.05)));
        }
    };

    const inputsData = [
        {
            label: "Visina",
            stateData: height,
            setStateData: setHeight,
        },
        {
            label: "Mere",
            stateData: size,
            setStateData: setSize,
        },
        {
            label: "Telesna Masa",
            stateData: bodyMass,
            setStateData: setBodyMass,
        },
        {
            label: "% masti",
            stateData: fatPercentage,
            setStateData: setFatPercentage,
        },
        {
            label: "% vode",
            stateData: waterPercentage,
            setStateData: setWaterPercentage,
        },
        {
            label: "% visceralnih masti",
            stateData: visceralFat,
            setStateData: setVisceralFat,
        },
        {
            label: "Mišićna masa",
            stateData: muscleMass,
            setStateData: setMuscleMass,
        },
        {
            label: "Ocena fizičke spremnosti",
            stateData: bodyFitnessIndex,
            setStateData: setBodyFitnessIndex,
        },
        {
            label: "Masa kostiju",
            stateData: boneMass,
            setStateData: setBoneMass,
        },
        {
            label: "Bazalni metabolizam",
            stateData: baseMetabolism,
            setStateData: setBaseMetabolism,
        },
        {
            label: "Metabolicka starost",
            stateData: metabolicAge,
            setStateData: setMetabolicAge,
        },
        {
            label: "Nadlaktica",
            stateData: upperArm,
            setStateData: setUpperArm,
        },
        {
            label: "Grudi",
            stateData: chest,
            setStateData: setChest,
        },
        {
            label: "Struk",
            stateData: waist,
            setStateData: setWaist,
        },
        {
            label: "Kukovi",
            stateData: hips,
            setStateData: setHips,
        },
        {
            label: "Butine",
            stateData: thighs,
            setStateData: setThighs,
        },
        {
            label: "Stomak",
            stateData: stomach,
            setStateData: setStomach,
        },
        {
            label: "Datum merenja",
            stateData: measurementDate,
            setStateData: setMeasurementDate,
        },
        {
            label: "Kalorijski unos od (kcal)",
            stateData: calorieFrom,
            setStateData: setCalorieFrom,
        },
        {
            label: "Kalorijski unos do (kcal)",
            stateData: calorieTo,
            setStateData: setCalorieTo,
        },
    ]

    const isNullOrWhitespace = value => {
        if (value instanceof String) {
            if (!value || value.trim() === '') {
                return true;
            }
        }

        return false;
    }

    const handleFormSubmit = e => {
        e.preventDefault();

        // Ako su makronutrijenti uneti, zbir mora biti 100%
        if (macroEntered && !macroValid) {
            alert("Zbir procenata makronutrijenata mora biti 100%. Trenutno je " + Math.round(macroSum) + "%.");
            return;
        }

        addMeasurement(measurementData, formValues, clientId);
        setIsOpen(false);
    }

    return (
        <>
            <h3 className='text-center py-2 bg-bluePrimary text-white shadow-md font-xs '>{modalTitle}</h3>
            <form className="flex flex-wrap w-full px-2">

                {inputsData.map(({ stateData, setStateData, label, type }, i) => (
                    <div className="mt-3 w-full md:w-6/12 2xl:w-4/12 px-2" key={i}>
                        {
                            type === 'textarea' &&
                            <div className="input-group" >
                                <label htmlFor={i} className="font-xs text-xs text-gray-500">{label}</label>
                                <textarea type="text" id={i} value={stateData} onChange={(e) => setStateData(e.target.value)} className="input-field" ></textarea>
                            </div>
                        }
                        {
                            type === 'checkbox' &&

                            <div className="input-group checkbox">
                                <input type="checkbox" id={i} value={stateData} onChange={(e) => setStateData(!stateData)} className="input-field" />
                                <label htmlFor={i} className="font-xs text-xs text-gray-500"><span className='check'><CheckIcon /></span>{label}</label>
                            </div>
                        }
                        {
                            type === 'label' &&
                            <div className="input-group">
                                <label htmlFor={i} className="font-xs text-xs text-gray-500">{label}</label>
                                <input type="text" id={i} value={stateData} className="input-field" />
                            </div>
                        }
                        {
                            type !== 'checkbox' && type !== 'textarea' && type !== 'label' &&
                            <div className="input-group">
                                <label htmlFor={i} className="font-xs text-xs text-gray-500">{label}</label>
                                <input type="text" id={i} value={stateData} onChange={(e) => setStateData(e.target.value)} className="input-field" />
                            </div>
                        }

                    </div>
                )
                )}
                <button type="submit" disabled style={{ display: "none" }} aria-hidden="true"></button>

                <div className="w-full px-2 mt-6">
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                        <div className="text-sm font-semibold text-gray-700 mb-3">Kalkulator: BMI, BMR i TDEE</div>

                        {(!clientGender || clientAge === null) &&
                            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-3">
                                Za BMR i TDEE su potrebni pol i datum rođenja iz kartona.
                                {!clientGender && ' Nedostaje pol.'}
                                {clientAge === null && ' Nedostaje ili je neispravan datum rođenja.'}
                                {' '}BMI se ipak može izračunati.
                            </div>}

                        <div className="flex flex-wrap items-end gap-3">
                            <div className="input-group" style={{ minWidth: '240px', flex: '1' }}>
                                <label className="font-xs text-xs text-gray-500">Nivo aktivnosti (za TDEE)</label>
                                <select value={activityFactor} onChange={(e) => setActivityFactor(e.target.value)} className="input-field">
                                    {ACTIVITY_LEVELS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                                </select>
                            </div>
                            <button type="button" onClick={runCalculator}
                                className="button without-icon bg-secondaryGradient" style={{ marginBottom: '2px' }}>
                                Izračunaj
                            </button>
                        </div>

                        {calcResult &&
                            <div className="flex flex-wrap gap-3 mt-4">
                                <div className="flex-1 text-center bg-white border border-gray-200 rounded-lg py-3" style={{ minWidth: '120px' }}>
                                    <div className="text-2xl font-semibold text-gray-800">{calcResult.bmi !== null ? calcResult.bmi : '—'}</div>
                                    <div className="text-xs text-gray-500 mt-1">BMI</div>
                                    {calcResult.bmi !== null && <div className="text-[11px] text-gray-400 mt-0.5">{calcResult.category}</div>}
                                </div>
                                <div className="flex-1 text-center bg-white border border-gray-200 rounded-lg py-3" style={{ minWidth: '120px' }}>
                                    <div className="text-2xl font-semibold text-gray-800">{calcResult.bmr !== null ? calcResult.bmr : '—'}</div>
                                    <div className="text-xs text-gray-500 mt-1">BMR (kcal)</div>
                                </div>
                                <div className="flex-1 text-center bg-white border border-gray-200 rounded-lg py-3" style={{ minWidth: '120px' }}>
                                    <div className="text-2xl font-semibold text-gray-800">{calcResult.tdee !== null ? calcResult.tdee : '—'}</div>
                                    <div className="text-xs text-gray-500 mt-1">TDEE (kcal)</div>
                                </div>
                            </div>}

                        {calcResult && calcResult.tdee !== null &&
                            <div className="text-[11px] text-gray-500 mt-3">
                                BMI je izračunat i biće sačuvan uz merenje. Predlog kalorijskog unosa (TDEE ±5%) upisan je u polja
                                „Kalorijski unos od/do" — po potrebi ga prilagodite cilju.
                            </div>}
                    </div>
                </div>

                <div className="w-full px-2 mt-4">
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                        <div className="text-sm font-semibold text-gray-700 mb-1">Odnos makronutrijenata u planu (%)</div>
                        <div className="text-xs text-gray-500 mb-3">Unesite procente — zbir mora biti 100.</div>
                        <div className="flex flex-wrap gap-3">
                            <div className="input-group" style={{ minWidth: '120px', flex: '1' }}>
                                <label className="font-xs text-xs text-gray-500">Ugljeni hidrati (%)</label>
                                <input type="text" value={carbsPercent} onChange={(e) => setCarbsPercent(e.target.value)} className="input-field" />
                            </div>
                            <div className="input-group" style={{ minWidth: '120px', flex: '1' }}>
                                <label className="font-xs text-xs text-gray-500">Proteini (%)</label>
                                <input type="text" value={proteinPercent} onChange={(e) => setProteinPercent(e.target.value)} className="input-field" />
                            </div>
                            <div className="input-group" style={{ minWidth: '120px', flex: '1' }}>
                                <label className="font-xs text-xs text-gray-500">Masti (%)</label>
                                <input type="text" value={fatPercent} onChange={(e) => setFatPercent(e.target.value)} className="input-field" />
                            </div>
                        </div>
                        {macroEntered &&
                            <div className={"text-xs mt-2 " + (macroValid ? "text-green-700" : "text-red-600")}>
                                Zbir: {Math.round(macroSum)}%{macroValid ? " ✓" : " — mora biti 100%"}
                            </div>}
                    </div>
                </div>

                <div className="mt-5 w-full md:w-6/12 2xl:w-4/12 px-4">
                    <button type="submit" className="button without-icon bg-secondaryGradient mx-auto mt-3" onClick={e => handleFormSubmit(e)}>
                        Sačuvaj
                    </button>
                </div>
                <div className="mt-5 w-full md:w-6/12 2xl:w-4/12 px-4">
                    <button type="submit" className="button without-icon bg-red-500 mx-auto mt-3" onClick={e => deleteMeasurement(clientId, measurementData.id)}>
                        Obriši
                    </button>
                </div>
            </form>
        </>
    )
}

export default MeasurementForm;
